import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CommonModal from "../../components/common/CommonModal";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FeeStructureDto, FeeInstallmentDto, FeeType, useFeeTypes, useCreateFeeType, useDeleteFeeType } from "../../hooks/useFeeStructure";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import { useError } from "../../contexts/ErrorContext";
import { useAuth } from "../../contexts/AuthContext";
import { useClasses } from "../../hooks/useClasses";
import { useSessions } from "../../hooks/useSession";

// Local type for form state
interface FeeInstallmentFormData {
  feeType: FeeType | null;
  amount: number | string; // Allow string for empty/partial input
  dueDate: string | null;
}

interface FeeStructureModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: FeeStructureDto;
  onSubmit: (formData: FeeStructureDto, selectedClassIds?: number[]) => void;
  isEdit?: boolean;
}

const FeeStructureModal: React.FC<FeeStructureModalProps> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit = false,
}) => {
  const { selectedSession: contextSession, selectedClass: contextClass } = useSessionClass();
  const { t } = useTranslation();
  const { showError } = useError();
  const { user, isTeacher } = useAuth();

  // Get schoolId
  const schoolId = useMemo(() => {
    return user?.schoolId;
  }, [user?.schoolId]);

  // Fetch classes and sessions
  const { classesQuery } = useClasses(schoolId ?? undefined);
  const { sessionsQuery } = useSessions(schoolId ?? undefined);

  // Fetch available fee types
  const { data: feeTypesList = [] } = useFeeTypes(schoolId ?? undefined, open);
  const createFeeTypeMutation = useCreateFeeType();
  const deleteFeeTypeMutation = useDeleteFeeType();

  const handleDeleteCustomFeeType = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this custom fee type?")) {
      try {
        await deleteFeeTypeMutation.mutateAsync(id);
        setFeeInstallments(prev => prev.map(inst => {
          if (inst.feeType && inst.feeType.id === id) {
            return { ...inst, feeType: null };
          }
          return inst;
        }));
      } catch (err: any) {
        showError(err?.response?.data?.message || "Failed to delete fee type (it might be in use)");
      }
    }
  };

  // Dialog state for adding a custom fee type
  const [addTypeDialogOpen, setAddTypeDialogOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Helper to derive base class name (without section)
  const getBaseClassName = useCallback((name?: string) => {
    if (!name) return "";
    return name.split(" ")[0];
  }, []);

  // Unique classes by base name (first occurrence wins)
  const uniqueClasses = useMemo(() => {
    const map = new Map<string, any>();
    (classesQuery.data || []).forEach((cls) => {
      const base = getBaseClassName(cls.name);
      if (base && !map.has(base)) {
        map.set(base, cls);
      }
    });
    return Array.from(map.values());
  }, [classesQuery.data, getBaseClassName]);

  // Local state for selected session and class in modal
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [selectedClassIds, setSelectedClassIds] = useState<number[]>([]);

  const [feeInstallments, setFeeInstallments] = useState<FeeInstallmentFormData[]>([
    {
      feeType: null,
      amount: "",
      dueDate: null,
    },
  ]);

  const hasDuplicateError = (index: number) => {
    const currentVal = feeInstallments[index].feeType;
    if (!currentVal) return false;

    return feeInstallments.some((inst, idx) => {
      if (idx === index) return false;
      return inst.feeType?.id === currentVal.id;
    });
  };

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setSelectedSessionId(initialData.sessionId || null);
        setSelectedClassId(initialData.classId || null);
        setSelectedClassIds([]);
        setFeeInstallments(
          initialData.feeInstallments && initialData.feeInstallments.length > 0
            ? initialData.feeInstallments.map(inst => {
              let dueDateValue: string | null = (inst.dueDate as any) ?? null;

              if (dueDateValue) {
                try {
                  let date: Date;

                  if (Array.isArray(dueDateValue)) {
                    const [year, month, day, hour = 0, minute = 0, second = 0] = dueDateValue;
                    date = new Date(year, month - 1, day, hour, minute, second);
                  } else if (typeof dueDateValue === 'string') {
                    date = new Date(dueDateValue);
                  } else if (typeof dueDateValue === 'number') {
                    date = new Date(dueDateValue);
                  } else {
                    date = new Date(dueDateValue as any);
                  }

                  dueDateValue = !isNaN(date.getTime()) ? date.toISOString() : null;
                } catch (error) {
                  dueDateValue = null;
                }
              } else {
                dueDateValue = null;
              }

              return {
                feeType: inst.feeTypeName ? { id: inst.feeTypeId, name: inst.feeTypeName } : null,
                amount: inst.amount === 0 ? "" : inst.amount,
                dueDate: dueDateValue
              };
            })
            : [
              {
                feeType: null,
                amount: "",
                dueDate: null,
              },
            ]
        );
      } else {
        setSelectedSessionId(contextSession?.id || null);
        setSelectedClassId(contextClass?.id || null);
        setSelectedClassIds(contextClass?.id ? [contextClass.id] : []);
        setFeeInstallments([
          {
            feeType: null,
            amount: "",
            dueDate: null,
          },
        ]);
      }
    }
  }, [open, initialData, isEdit, contextSession?.id, contextClass?.id]);

  useEffect(() => {
    if (open && selectedSessionId && classesQuery.data && !selectedClassId && selectedClassIds.length === 0) {
      const availableClasses = classesQuery.data.filter((cls) => {
        const classSessionId = cls.sessionId;
        if (classSessionId === null || classSessionId === undefined) {
          return true;
        }
        return Number(classSessionId) === Number(selectedSessionId);
      });

      if (availableClasses.length > 0) {
        setSelectedClassId(availableClasses[0].id || null);
        if (availableClasses[0].id && !isEdit) {
          setSelectedClassIds([availableClasses[0].id]);
        }
      }
    }
  }, [open, selectedSessionId, classesQuery.data, selectedClassId, selectedClassIds, isEdit]);

  const handleAddInstallment = () => {
    setFeeInstallments([
      ...feeInstallments,
      {
        feeType: null,
        amount: "",
        dueDate: null,
      },
    ]);
  };

  const handleRemoveInstallment = (index: number) => {
    if (feeInstallments.length > 1) {
      const updated = feeInstallments.filter((_, i) => i !== index);
      setFeeInstallments(updated);
    }
  };

  const handleInstallmentChange = (
    index: number,
    field: keyof FeeInstallmentFormData,
    value: any
  ) => {
    const updated = [...feeInstallments];
    if (field === "amount") {
      if (value === "") {
        updated[index] = {
          ...updated[index],
          [field]: "",
        };
      } else {
        if (value.toString().includes(".")) {
          const intValue = parseInt(value.toString().split(".")[0], 10);
          if (!isNaN(intValue) && intValue >= 0) {
            updated[index] = {
              ...updated[index],
              [field]: intValue.toString(),
            };
          } else {
            return;
          }
        } else {
          const intValue = parseInt(value, 10);
          if (!isNaN(intValue) && intValue >= 0) {
            updated[index] = {
              ...updated[index],
              [field]: intValue.toString(),
            };
          } else {
            updated[index] = {
              ...updated[index],
              [field]: value,
            };
          }
        }
      }
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
    }
    setFeeInstallments(updated);
  };

  const handleCloseAddTypeDialog = () => {
    setAddTypeDialogOpen(false);
    setNewTypeName("");
    setActiveIndex(null);
  };

  const handleSaveCustomFeeType = async () => {
    if (!newTypeName.trim()) return;
    try {
      const response = await createFeeTypeMutation.mutateAsync({
        name: newTypeName.trim(),
        schoolId: schoolId ?? undefined,
      });
      if (activeIndex !== null) {
        handleInstallmentChange(activeIndex, "feeType", response);
      }
      handleCloseAddTypeDialog();
    } catch (err: any) {
      showError(err?.response?.data?.message || "Failed to save fee type");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedSessionId) {
      showError(t("please_select_a_session") || "Please select a session");
      return;
    }

    if (isEdit && !selectedClassId) {
      showError(t("please_select_a_class") || "Please select a class");
      return;
    }

    if (!isEdit && selectedClassIds.length === 0) {
      showError(t("please_select_at_least_one_class") || "Please select at least one class");
      return;
    }

    if (feeInstallments.length === 0) {
      showError(t("please_add_at_least_one_fee") || "Please add at least one fee");
      return;
    }

    // Validate fee blocks
    for (let i = 0; i < feeInstallments.length; i++) {
      const inst = feeInstallments[i];
      const amountValue = typeof inst.amount === "string" ? parseInt(inst.amount, 10) : Math.floor(inst.amount);
      if (!amountValue || amountValue <= 0 || isNaN(amountValue)) {
        showError(`Fee ${i + 1}: Amount must be greater than 0`);
        return;
      }
      if (!inst.dueDate) {
        showError(`Fee ${i + 1}: Due date is required`);
        return;
      }
      const dueDateObj = new Date(inst.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dueDateObj < today) {
        showError(`Fee ${i + 1}: Due date cannot be in the past`);
        return;
      }
      if (!inst.feeType) {
        showError(`Fee ${i + 1}: Fee type is required`);
        return;
      }
      if (hasDuplicateError(i)) {
        showError(`Fee ${i + 1}: Duplicate fee type selected`);
        return;
      }
    }

    const processedInstallments: FeeInstallmentDto[] = feeInstallments.map((inst): FeeInstallmentDto => ({
      feeTypeId: inst.feeType!.id,
      feeTypeName: inst.feeType!.name,
      amount: typeof inst.amount === "string" ? (parseInt(inst.amount, 10) || 0) : Math.floor(inst.amount),
      dueDate: inst.dueDate || "",
    }));

    const firstClassId = isEdit ? selectedClassId! : selectedClassIds[0];
    const selectedClass = classesQuery.data?.find((cls) => cls.id === firstClassId);
    const className = selectedClass ? getBaseClassName(selectedClass.name) : "";

    const formData: FeeStructureDto = {
      sessionId: selectedSessionId,
      classId: firstClassId,
      totalAnnualFees: 0.0,
      totalInstallments: processedInstallments.length,
      className: className,
      feeInstallments: processedInstallments,
    };

    onSubmit(formData, isEdit ? undefined : selectedClassIds);
  };

  const isFormValid =
    selectedSessionId !== null &&
    (isEdit ? selectedClassId !== null : selectedClassIds.length > 0) &&
    feeInstallments.length > 0 &&
    feeInstallments.every((inst, index) => {
      const amountValue = typeof inst.amount === "string" ? parseInt(inst.amount, 10) : Math.floor(inst.amount);
      const hasType = inst.feeType !== null;
      return amountValue > 0 && inst.dueDate && hasType && !hasDuplicateError(index);
    });

  const filteredClasses = useMemo(() => {
    if (!classesQuery.data) return [];
    if (!selectedSessionId) {
      return classesQuery.data;
    }
    return classesQuery.data.filter((cls) => {
      const classSessionId = cls.sessionId;
      if (classSessionId === null || classSessionId === undefined) {
        return true;
      }
      return Number(classSessionId) === Number(selectedSessionId);
    });
  }, [classesQuery.data, selectedSessionId]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <CommonModal
        open={open}
        onClose={handleClose}
        title={isEdit ? t("edit_fee_structure") || "Edit Fee Structure" : t("add_fee_structure") || "Add Fee Structure"}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        contentSx={{ mt: 2 }}
        customActions={
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isFormValid}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {isEdit ? t("update") || "Update" : t("add") || "Add"}
            </Button>
          </>
        }
      >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              {isEdit ? (
                <TextField
                  fullWidth
                  label="Session"
                  value={
                    sessionsQuery.data?.find((s) => s.id === selectedSessionId)?.name || "-"
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Session *</InputLabel>
                  <Select
                    value={selectedSessionId || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedSessionId(value === "" ? null : Number(value));
                      setSelectedClassId(null);
                    }}
                    label="Session *"
                    required
                  >
                    <MenuItem value="">
                      <em>{t("select_session") || "Select Session"}</em>
                    </MenuItem>
                    {sessionsQuery.data?.map((session) => (
                      <MenuItem key={session.id} value={session.id}>
                        {session.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              {isEdit ? (
                <TextField
                  fullWidth
                  label="Class"
                  value={
                    (() => {
                      const selectedClass = classesQuery.data?.find((cls) => cls.id === selectedClassId);
                      if (!selectedClass) return "-";
                      return getBaseClassName(selectedClass.name) || selectedClass.name;
                    })()
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Class *</InputLabel>
                  <Select
                    multiple={!isEdit}
                    value={isEdit ? (selectedClassId || "") : selectedClassIds}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (isEdit) {
                        setSelectedClassId(value === "" ? null : Number(value));
                      } else {
                        const selectedIds = typeof value === "string" ? value.split(",").map(Number) : (value as number[]);
                        setSelectedClassIds(selectedIds);
                      }
                    }}
                    renderValue={(selected) => {
                      if (isEdit) {
                        const selectedClass = classesQuery.data?.find((cls) => cls.id === selected);
                        return selectedClass ? (getBaseClassName(selectedClass.name) || selectedClass.name) : "";
                      } else {
                        const selectedList = (selected as number[]).map(id => {
                          const cls = classesQuery.data?.find(c => c.id === id);
                          return cls ? (getBaseClassName(cls.name) || cls.name) : "";
                        });
                        return selectedList.join(", ");
                      }
                    }}
                    label="Class *"
                    displayEmpty
                    disabled={filteredClasses.length === 0}
                    required
                  >
                    {!isEdit && filteredClasses.length > 0 && (
                      <MenuItem
                        value="all"
                        onClick={() => {
                          const allClassIds = uniqueClasses.map(cls => cls.id).filter(Boolean) as number[];
                          if (selectedClassIds.length === allClassIds.length) {
                            setSelectedClassIds([]); // Deselect all
                          } else {
                            setSelectedClassIds(allClassIds); // Select all
                          }
                        }}
                      >
                        <Box sx={{ fontWeight: "bold" }}>
                          {selectedClassIds.length === uniqueClasses.length ? t("deselect_all", "Deselect All") : t("select_all", "Select All")}
                        </Box>
                      </MenuItem>
                    )}
                    {uniqueClasses.map((cls) => {
                      const isSelected = !isEdit && selectedClassIds.includes(cls.id);
                      return (
                        <MenuItem key={cls.id} value={cls.id} sx={{ fontWeight: isSelected ? "bold" : "regular" }}>
                          {!isEdit && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              style={{ marginRight: 8 }}
                            />
                          )}
                          {getBaseClassName(cls.name) || cls.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  mt: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  Fees
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddInstallment}
                  variant="outlined"
                  size="small"
                >
                  Add Fee
                </Button>
              </Box>
            </Grid>

            {feeInstallments.map((installment, index) => (
              <Grid item xs={12} key={index}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Fee Item {index + 1}
                    </Typography>
                    {feeInstallments.length > 1 && (
                      <IconButton
                        onClick={() => handleRemoveInstallment(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth error={hasDuplicateError(index)}>
                        <InputLabel>Fee Type *</InputLabel>
                        <Select
                          value={installment.feeType ? installment.feeType.id : ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "ADD_NEW") {
                              setActiveIndex(index);
                              setAddTypeDialogOpen(true);
                            } else {
                              const found = feeTypesList.find((t) => t.id === val);
                              handleInstallmentChange(index, "feeType", found || null);
                            }
                          }}
                          label="Fee Type *"
                          renderValue={(selectedId) => {
                            const found = feeTypesList.find((t) => t.id === selectedId);
                            if (!found) return "";
                            return found.name === "TUITION_FEES"
                              ? "Tuition Fees"
                              : found.name === "ADMISSION_FEES"
                              ? "Admission Fees"
                              : found.name === "TRANSPORT_FEES"
                              ? "Transport Fees"
                              : found.name;
                          }}
                        >
                          {feeTypesList.map((type) => (
                            <MenuItem 
                              key={type.id} 
                              value={type.id}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <span>
                                {type.name === "TUITION_FEES"
                                  ? "Tuition Fees"
                                  : type.name === "ADMISSION_FEES"
                                  ? "Admission Fees"
                                  : type.name === "TRANSPORT_FEES"
                                  ? "Transport Fees"
                                  : type.name}
                              </span>
                              {type.schoolId && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCustomFeeType(type.id);
                                  }}
                                  sx={{ ml: 2 }}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              )}
                            </MenuItem>
                          ))}
                          <MenuItem
                            value="ADD_NEW"
                            sx={{
                              color: "primary.main",
                              fontWeight: "bold",
                              borderTop: "1px solid #e0e0e0",
                              mt: 1,
                              "&:hover": {
                                backgroundColor: "#f5f9ff",
                              },
                            }}
                          >
                            + Add New Fee Type
                          </MenuItem>
                        </Select>
                        {hasDuplicateError(index) && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            This fee type is selected more than once
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box
                        onWheel={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        sx={{ width: '100%' }}
                      >
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={typeof installment.amount === "string" ? installment.amount : (installment.amount === 0 ? "" : installment.amount)}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            handleInstallmentChange(
                              index,
                              "amount",
                              inputValue === "" ? "" : inputValue
                            );
                          }}
                          onWheel={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            (e.target as HTMLInputElement).blur();
                          }}
                          onFocus={(e) => {
                            const input = e.target as HTMLInputElement;
                            const handleWheel = (wheelEvent: WheelEvent) => {
                              wheelEvent.preventDefault();
                              wheelEvent.stopPropagation();
                              input.blur();
                            };
                            input.addEventListener('wheel', handleWheel, { passive: false });
                            const handleBlur = () => {
                              input.removeEventListener('wheel', handleWheel);
                              input.removeEventListener('blur', handleBlur);
                            };
                            input.addEventListener('blur', handleBlur);
                          }}
                          inputProps={{
                            min: 0,
                            step: 1,
                            style: {
                              MozAppearance: 'textfield'
                            }
                          }}
                          sx={{
                            '& input[type=number]': {
                              MozAppearance: 'textfield'
                            },
                            '& input[type=number]::-webkit-outer-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0
                            }
                          }}
                          required
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="Due Date"
                        disablePast
                        value={
                          installment.dueDate
                            ? (() => {
                              try {
                                const date = new Date(installment.dueDate);
                                return isNaN(date.getTime()) ? null : date;
                              } catch (error) {
                                return null;
                              }
                            })()
                            : null
                        }
                        onChange={(newValue) => {
                          if (newValue) {
                            const year = newValue.getFullYear();
                            const month = String(newValue.getMonth() + 1).padStart(2, '0');
                            const day = String(newValue.getDate()).padStart(2, '0');
                            const hours = String(newValue.getHours()).padStart(2, '0');
                            const minutes = String(newValue.getMinutes()).padStart(2, '0');
                            const seconds = String(newValue.getSeconds()).padStart(2, '0');
                            const localDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
                            handleInstallmentChange(
                              index,
                              "dueDate",
                              localDateTime
                            );
                          }
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.blur();
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
      </CommonModal>

      {/* Add Custom Fee Type Dialog */}
      <Dialog open={addTypeDialogOpen} onClose={handleCloseAddTypeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Add New Fee Type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Fee Type Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
            placeholder="e.g. Library Fees"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddTypeDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveCustomFeeType}
            variant="contained"
            disabled={!newTypeName.trim() || createFeeTypeMutation.isLoading}
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" },
            }}
          >
            {createFeeTypeMutation.isLoading ? "Saving..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default FeeStructureModal;
